import type { UseQueryResult } from 'react-query';
import { HEATMAP_GENERATION_FAILURE_ERRORS } from '@hotjar/heatmaps';
import { moment } from '@hotjar/insights-common/lib/moment';

const NUMBER_OF_DAYS = 7;
const COVERAGE_UPSELL_THRESHOLD = 20; // % percent of coverage

// TODO: This is copied from legacy common. Add it to the common package, use it from there, and deprecate the legacy one.
/**
 * Return how much time difference in days there is between a date and now().
 *
 * @param epochTime The epoch time to base calculation on.
 * @param limitToZero Limit the lowest returnable value to 0.
 */
function daysFromNow(epochTime: number, limitToZero?: boolean): number {
  const difference = moment(epochTime * 1000).diff(moment(), 'days', true);

  return limitToZero && difference < 0 ? 0 : difference;
}

export const isWithinLast7Days = (timestamp: number): boolean => {
  return Math.abs(daysFromNow(timestamp)) <= NUMBER_OF_DAYS;
};

export const getReportError = ({
  screenshotData,
  heatData,
  takeScreenshotData,
  coveragePercentage,
}: {
  screenshotData: UseQueryResult<HeatmapAPI.GetScreenshotDataResponse, Error>;
  heatData: UseQueryResult<HeatmapAPI.GetHeatDataResponse<HeatmapDataType>, Error>;
  takeScreenshotData: UseQueryResult<HeatmapAPI.Response, Error>;
  coveragePercentage: number;
}): HeatmapGenerationError | undefined => {
  const err = screenshotData?.error || takeScreenshotData?.error || heatData?.error;
  if (
    err instanceof Error &&
    err.message === HEATMAP_GENERATION_FAILURE_ERRORS.SCREENSHOT_GENERATION_FAILURE
  ) {
    return HEATMAP_GENERATION_FAILURE_ERRORS.SCREENSHOT_GENERATION_FAILURE;
  }

  if (
    err instanceof Error &&
    err.message === HEATMAP_GENERATION_FAILURE_ERRORS.HEATMAP_GENERATION_FAILURE
  ) {
    return HEATMAP_GENERATION_FAILURE_ERRORS.HEATMAP_GENERATION_FAILURE;
  }

  if (
    err instanceof Error &&
    err.message === HEATMAP_GENERATION_FAILURE_ERRORS.NO_HEATMAP_DATA_FOUND
  ) {
    if (!screenshotData?.data?.firstSessionCreated) {
      return HEATMAP_GENERATION_FAILURE_ERRORS.TRACKING_CODE_INSTALLED_NO_SESSIONS;
    }

    if (isWithinLast7Days(screenshotData?.data?.firstSessionCreated)) {
      return HEATMAP_GENERATION_FAILURE_ERRORS.NO_SESSIONS_FOR_URL;
    }

    if (
      screenshotData?.data?.lastSessionCreated &&
      !isWithinLast7Days(screenshotData.data.lastSessionCreated)
    ) {
      return HEATMAP_GENERATION_FAILURE_ERRORS.NO_HEATMAP_DATA_CHECK_TRACKING;
    }

    if (
      screenshotData?.data?.lastSessionCreated &&
      isWithinLast7Days(screenshotData.data.lastSessionCreated) &&
      coveragePercentage <= COVERAGE_UPSELL_THRESHOLD
    ) {
      return HEATMAP_GENERATION_FAILURE_ERRORS.NO_HEATMAP_DATA_INCREASE_COVERAGE;
    }

    return HEATMAP_GENERATION_FAILURE_ERRORS.NO_HEATMAP_DATA_FOUND;
  }

  if (
    err instanceof Error &&
    err.message === HEATMAP_GENERATION_FAILURE_ERRORS.REGEX_MATCH_NOT_SUPPORTED
  ) {
    return HEATMAP_GENERATION_FAILURE_ERRORS.REGEX_MATCH_NOT_SUPPORTED;
  }

  // When we don't know the error, we return undefined,
  // which ends up being used to call the 'renderErrorMessage' function.
  // TODO: Consider making this more explicity with an 'Unknown' type.
  return undefined;
};
